/* eslint-disable max-len */
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { debounce } from 'lodash';
import styled from 'styled-components';

import Input from '@quintoandar/cozy-core/Input/Input';
import Box from '@quintoandar/cozy-core/Box';
import Typography from '@quintoandar/cozy-core/Typography';
import tokens from '@quintoandar/cozy-theme/tokens/js';
import Checkbox from '@quintoandar/cozy-core/Checkbox';

import ErrorBoundary from 'components/ErrorBoundary';
import { logUncaught } from 'utils/log';
import { selectHouseLatLng } from 'containers/PhotoSessionDetail/selectors';
import { isInvalidNames } from 'utils/invalidNames';

import ListPredications from './ListPredications';
import messages from './messages';

export const CustomInput = styled(Input)`
    margin-bottom: 0;
`;

const AutoComplete = ({ intl, latLng, showCheckbox, handleCondoName, inputValue, checkboxChecked, onChangeCheckbox }) => {
  const [input, setInput] = useState(inputValue);
  const [checkbox, setCheckbox] = useState(false);
  const [invalidName, setInvalidName] = useState(false);
  const [shouldShowSuggestion, setShouldShowSuggestion] = useState(false);
  const inputRef = React.createRef();

  const invalidNameVerification = useCallback((value) => {
    if (isInvalidNames(value)) {
      handleCondoName(''); setInvalidName(true);
    } else {
      handleCondoName(value);
    }
  }, []);

  const debounceInvalidNameVerification = useCallback(debounce(invalidNameVerification, 500), []);

  const handleInput = useCallback((event) => {
    const { value } = event?.target;
    setInvalidName(false);
    setInput(value);
    setShouldShowSuggestion(true);
    debounceInvalidNameVerification(value);
  }, []);

  const handleClick = useCallback((value) => {
    handleCondoName(value);
    setShouldShowSuggestion(false);
    setInput(value);
  }, []);

  const handleCheckbox = useCallback(() => {
    const newStateToCheckbox = !checkbox;
    setCheckbox(newStateToCheckbox);
    onChangeCheckbox(newStateToCheckbox);
    setInvalidName(false);

    setInput('');

    if (newStateToCheckbox) {
      handleCondoName(null);
    } else {
      handleCondoName('');
    }
  }, [checkbox]);

  const onBlur = useCallback(() => {
    setTimeout(() => {
      setShouldShowSuggestion(false);
    }, 300);
  }, []);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    setCheckbox(checkboxChecked);
  }, [checkboxChecked]);

  return (
    <Fragment>
      <Typography mt={tokens.alias.spacing.small}>
        <FormattedMessage {...messages.label} />
      </Typography>
      <Box>
        <CustomInput
          fullWidth
          value={input || ''}
          onChange={handleInput}
          ref={inputRef}
          disabled={checkbox}
          onBlur={onBlur}
          placeholder={intl.formatMessage(messages.placeholder)}
        />
        {invalidName ? <Typography kind="critical">
          <FormattedMessage {...messages.invalidName} />
        </Typography> : null
        }
      </Box>
      {shouldShowSuggestion && <ErrorBoundary errorComponent={<></>} logError={logUncaught}>
        <ListPredications intl={intl} input={input} latLng={latLng} handleClick={handleClick} />
      </ErrorBoundary>
      }
      {showCheckbox ?
        <Box mt={tokens.alias.spacing.xxsmall}>
          <Checkbox
            name="IDonTKnow"
            label={intl.formatMessage(messages.iDoNotKnowTheName)}
            checked={checkbox}
            onChange={handleCheckbox}
          />
        </Box>
        : null}
    </Fragment>
  );
};

export const mapStateToProps = createStructuredSelector({
  latLng: selectHouseLatLng,
});

AutoComplete.propTypes = {
  inputValue: PropTypes.string,
  intl: PropTypes.object,
  latLng: PropTypes.object,
  showCheckbox: PropTypes.bool,
  handleCondoName: PropTypes.func,
  checkboxChecked: PropTypes.bool,
  onChangeCheckbox: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(mapStateToProps, null)
)(AutoComplete);
